import React from 'react';
import clsx from 'clsx';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

import type { CaraWorkspaceFieldsFragment } from '~/api/graphql';
import { getPresetIconChip } from '~/components/workspaces/lib/preset-icon';
import { familyForPreset } from '~/components/home/use-case-catalog';

/**
 * Title segment of the workspace top bar. Shows the preset icon chip, an
 * inline-editable title, and a metadata sub-line with the preset name, client
 * name (when known), and workspace owner (when known).
 *
 * Edit affordances:
 * - The title row has a faint hover background and a pencil icon that fades in
 *   on hover, so the click-to-rename pattern stays discoverable without
 *   shouting at users in the resting state.
 * - When the workspace title is empty, editing is disabled to avoid races with
 *   the system's async title generation. The placeholder communicates this.
 */
export const WorkspaceTopBarTitle = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  onRename: (nextTitle: string) => Promise<boolean> | boolean;
  disabled?: boolean;
  /**
   * Monotonically-increasing counter set by the parent. Each new value puts
   * the title into edit mode (used by the overflow menu's "Rename" item).
   */
  editRequest?: number;
}) => {
  const { workspace } = props;
  const hasTitle = workspace.title.trim().length > 0;
  const editingDisabled = !hasTitle || !!props.disabled;

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(workspace.title);

  // Keep the local draft in sync with the upstream workspace title whenever we
  // are not actively editing (e.g. the title gets backfilled by the system or
  // changed elsewhere).
  React.useEffect(() => {
    if (!editing) setDraft(workspace.title);
  }, [workspace.title, editing]);

  const startEditing = React.useCallback(() => {
    if (editingDisabled) return;
    setDraft(workspace.title);
    setEditing(true);
    // Focus on next tick so the input is mounted.
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [editingDisabled, workspace.title]);

  // External rename trigger (overflow menu item). We watch the request counter
  // and enter edit mode when it changes, after the first render.
  const editRequest = props.editRequest;
  const lastSeenRequest = React.useRef(editRequest);
  React.useEffect(() => {
    if (editRequest === undefined) return;
    if (editRequest === lastSeenRequest.current) return;
    lastSeenRequest.current = editRequest;
    startEditing();
  }, [editRequest, startEditing]);

  const cancelEditing = () => {
    setDraft(workspace.title);
    setEditing(false);
  };

  const commitEditing = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === workspace.title) {
      cancelEditing();
      return;
    }
    const ok = await props.onRename(trimmed);
    if (!ok) {
      cancelEditing();
      return;
    }
    setEditing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  const metadataParts = buildMetadataParts(workspace);

  return (
    <div className="flex flex-row items-center gap-2.5 min-w-0 flex-1">
      <div className="shrink-0">{getPresetIconChip(workspace.preset)}</div>
      <div className="flex flex-col min-w-0 flex-1 leading-tight">
        {editing ? (
          // Edit mode. Identical padding to the view-mode button keeps bar
          // height stable. The input fills the available width so text is
          // never clipped or scrolled.
          <div className="-mx-1.5 px-1.5 py-0.5 min-w-0 overflow-hidden">
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitEditing}
              onKeyDown={onKeyDown}
              className="w-full bg-transparent border-none outline-none text-sm font-medium leading-tight text-neutral-900 dark:text-white focus:ring-0 p-0"
              aria-label="Workspace title"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            disabled={editingDisabled}
            title={editingDisabled ? undefined : 'Click to rename'}
            aria-label={editingDisabled ? 'Workspace title' : 'Rename workspace'}
            className={clsx(
              'group flex flex-row items-center gap-1.5 -mx-1.5 px-1.5 py-0.5 rounded-md text-left w-fit transition-colors',
              !editingDisabled &&
                'hover:bg-neutral-100 dark:hover:bg-white/10 focus-visible:bg-neutral-100 dark:focus-visible:bg-white/10 focus-visible:outline-none cursor-text'
            )}
          >
            <span
              className={clsx(
                'text-sm font-medium truncate min-w-0',
                hasTitle
                  ? 'text-neutral-900 dark:text-white'
                  : 'text-neutral-400 dark:text-neutral-500 italic'
              )}
            >
              {hasTitle ? workspace.title : 'Naming workspace…'}
            </span>
            {!editingDisabled && (
              <PencilSquareIcon
                aria-hidden="true"
                className="size-3.5 text-neutral-400 dark:text-neutral-500 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0"
              />
            )}
          </button>
        )}
        {metadataParts.length > 0 && (
          <div className="flex flex-row items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 truncate">
            {metadataParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span aria-hidden="true" className="text-neutral-300 dark:text-neutral-600">
                    ·
                  </span>
                )}
                <span className="truncate">{part}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const buildMetadataParts = (workspace: CaraWorkspaceFieldsFragment): string[] => {
  const presetTitle =
    workspace.customPreset?.title ||
    (workspace.preset ? familyForPreset(workspace.preset)?.title : undefined);
  const clientName = workspace.metadata?.clientName?.trim() || undefined;
  const owner = workspace.agencyUser?.name?.trim() || undefined;

  return [presetTitle, clientName, owner].filter((s): s is string => !!s && s.length > 0);
};
